import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import SubscriptionsOutlinedIcon from '@mui/icons-material/SubscriptionsOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useStripeApi } from '../../hooks/useStripeApi';
import { fetchStripeOverviewMetricsResolved, fetchStripeDailySeriesResolved } from '../../services/stripeResolved';
import { StripeBillingActions } from '../../components/stripe/StripeBillingActions';
import { fmtStripeMoney, StripeHero, StripeKpiCard } from '../../components/stripe/stripeShared';

type Overview = NonNullable<Awaited<ReturnType<typeof fetchStripeOverviewMetricsResolved>>>;

const StripeOverviewPage: React.FC = () => {
  const { settings, isSecretConfigured } = useStripeApi();
  const navigate = useNavigate();
  const [overview, setOverview] = useState<Overview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [chartSeries, setChartSeries] = useState<{ date: string; amount: number }[]>([]);
  const [bannerMsg, setBannerMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [ov, series] = await Promise.all([
        fetchStripeOverviewMetricsResolved(settings, isSecretConfigured),
        fetchStripeDailySeriesResolved(settings, isSecretConfigured, 14),
      ]);
      setOverview(ov);
      setChartSeries(series);
    } catch (e) {
      const msg =
        e instanceof Error
          ? e.message
          : axios.isAxiosError(e)
            ? e.response?.data?.error?.message || e.message
            : 'Failed to load Stripe';
      setError(String(msg));
      setOverview(null);
    } finally {
      setLoading(false);
    }
  }, [settings, isSecretConfigured]);

  useEffect(() => {
    void load();
  }, [load]);

  const balancePrimary = useMemo(() => {
    const a = overview?.balance?.available?.[0];
    if (!a) {
      return null;
    }
    return fmtStripeMoney(a.amount, a.currency);
  }, [overview]);

  const chartData = useMemo(
    () => chartSeries.map((d) => ({ ...d, label: d.date.slice(5) })),
    [chartSeries]
  );

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2} sx={{ mb: 2 }}>
        <StripeHero
          compact
          title="Stripe · Overview"
          subtitle="Balance, MRR, and charge volume — jump to sub-pages for transactions, customers, subscriptions, and payouts."
        />
        <Button
          variant="outlined"
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />}
          onClick={() => void load()}
          disabled={loading}
        >
          Refresh
        </Button>
      </Stack>

      <StripeBillingActions onMessage={setBannerMsg} onSuccess={() => void load()} />

      {bannerMsg && (
        <Alert severity={bannerMsg.type} sx={{ mb: 2 }} onClose={() => setBannerMsg(null)}>
          {bannerMsg.text}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <StripeKpiCard
            icon={<AccountBalanceWalletOutlinedIcon sx={{ fontSize: 28, color: '#635bff' }} />}
            title="Available balance"
            loading={loading}
            value={balancePrimary || '—'}
            hint={overview?.balance?.available && overview.balance.available.length > 1 ? 'Primary currency' : undefined}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <StripeKpiCard
            icon={<TrendingUpOutlinedIcon sx={{ fontSize: 28, color: '#0d9488' }} />}
            title="Est. MRR"
            loading={loading}
            value={overview ? fmtStripeMoney(overview.mrrCents, overview.mrrCurrency) : '—'}
            hint="Active subs (first 100)"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <StripeKpiCard
            icon={<PeopleOutlineIcon sx={{ fontSize: 28, color: '#7c3aed' }} />}
            title="Customers"
            loading={loading}
            value={overview ? String(overview.customersOnPage) : '—'}
            hint={overview?.customersHasMore ? 'First page — see Customers' : 'Listed on first page'}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <StripeKpiCard
            icon={<SubscriptionsOutlinedIcon sx={{ fontSize: 28, color: '#db2777' }} />}
            title="Active subscriptions"
            loading={loading}
            value={overview ? String(overview.activeSubscriptions) : '—'}
            hint="status=active"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <StripeKpiCard
            icon={<ReceiptLongOutlinedIcon sx={{ fontSize: 28, color: '#ea580c' }} />}
            title="Paid charges (30d)"
            loading={loading}
            value={overview ? fmtStripeMoney(overview.paidCharges30dCents, overview.chargesCurrency) : '—'}
            hint={`${overview?.paidCharges30dCount ?? 0} charges`}
          />
        </Grid>
      </Grid>

      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Typography variant="subtitle1" fontWeight={800} gutterBottom>
          Successful charge volume (14 days)
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          Built from the latest paid charges returned by the API (up to 100).
        </Typography>
        {loading && !chartData.length ? (
          <Skeleton variant="rounded" height={260} />
        ) : chartData.length ? (
          <Box sx={{ width: '100%', height: 280 }}>
            <ResponsiveContainer>
              <AreaChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="stripeVol" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#635bff" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#635bff" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" tickFormatter={(v) => `$${v}`} />
                <RTooltip
                  formatter={(value) => {
                    const n = typeof value === 'number' ? value : Number(value);
                    return [`$${(Number.isNaN(n) ? 0 : n).toFixed(2)}`, 'Volume'];
                  }}
                />
                <Area type="monotone" dataKey="amount" stroke="#635bff" strokeWidth={2} fill="url(#stripeVol)" />
              </AreaChart>
            </ResponsiveContainer>
          </Box>
        ) : (
          <Typography color="text.secondary">No charge data in range.</Typography>
        )}
      </Paper>

      <Stack direction="row" flexWrap="wrap" gap={1}>
        <Button variant="contained" onClick={() => navigate('/stripe/transactions')}>
          Transactions
        </Button>
        <Button variant="outlined" onClick={() => navigate('/stripe/customers')}>
          Customers
        </Button>
        <Button variant="outlined" onClick={() => navigate('/stripe/subscriptions')}>
          Subscriptions
        </Button>
        <Button variant="outlined" onClick={() => navigate('/stripe/payouts')}>
          Payouts
        </Button>
      </Stack>
    </Box>
  );
};

export default StripeOverviewPage;
